import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import SupplierRegistrationForm from "@/components/SupplierRegistrationForm";
import SkylineHero from "@/components/SkylineHero";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const Procurement = () => {
  const [isRegistrationFormOpen, setIsRegistrationFormOpen] = useState(false);
  const { t } = useLanguage();
  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="min-h-[50vh] sm:min-h-[60vh] md:min-h-[80vh] flex items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background relative overflow-hidden">
        {/* Skyline drawing animation — matches hotels hero */}
        <SkylineHero />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:32px_32px] sm:bg-[size:64px_64px] opacity-20"></div>
        <div className="absolute -top-16 sm:-top-32 -right-16 sm:-right-32 w-32 h-32 sm:w-64 sm:h-64 md:w-80 md:h-80 bg-primary/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-16 sm:-bottom-32 -left-16 sm:-left-32 w-32 h-32 sm:w-64 sm:h-64 md:w-80 md:h-80 bg-secondary/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        
        {/* Back button */}
        <Link 
          to="/"
          className="absolute top-2 left-2 sm:top-4 sm:left-4 md:top-6 md:left-6 flex items-center text-muted-foreground hover:text-foreground transition-all duration-300 z-10 hover-scale group bg-background/80 backdrop-blur-sm rounded-full px-2 py-1 sm:px-3 sm:py-2 border border-border/50"
        >
          <ArrowLeft className="mr-1 transition-transform duration-300 group-hover:-translate-x-1" size={14} />
          <span className="text-xs">{t("common.backToHome")}</span>
        </Link>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-6xl mx-auto text-center">
            <div className="space-y-3 sm:space-y-4 md:space-y-6">
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-light leading-tight tracking-tight text-foreground">
                  Brassbell Procurement
                </h1>
                <div className="w-8 sm:w-12 md:w-16 lg:w-20 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
              </div>
              <p className="text-sm sm:text-base md:text-lg font-light text-foreground max-w-2xl sm:max-w-3xl mx-auto px-2">
                Partner with us to deliver exceptional quality and value across our portfolio. Join our network of trusted suppliers and service providers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-4 sm:left-10 w-16 h-16 sm:w-32 sm:h-32 border border-black/10 rounded-full"></div>
          <div className="absolute bottom-20 right-4 sm:right-10 w-24 h-24 sm:w-48 sm:h-48 border border-black/5 rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 sm:w-96 sm:h-96 border border-black/5 rounded-full"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          {/* Main Cards Section - Mobile-First Layout */}
          <div className="max-w-7xl mx-auto mb-12 sm:mb-16 md:mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8 items-stretch">
              {/* Left Card - Larger */}
              <div className="lg:col-span-7">
                <div className="h-full bg-black text-white p-4 sm:p-6 md:p-8 lg:p-12 rounded-2xl sm:rounded-3xl shadow-2xl relative overflow-hidden group hover:shadow-3xl transition-all duration-500">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
                  <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-white/5 rounded-full blur-xl"></div>
                  
                  <div className="relative z-10">
                    <div className="mb-4 sm:mb-6">
                      <div className="w-12 sm:w-16 h-1 bg-white mb-4 sm:mb-6"></div>
                      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light mb-3 sm:mb-4">Supplier Registration</h2>
                      <p className="text-white/80 text-sm sm:text-base md:text-lg leading-relaxed mb-6 sm:mb-8">
                        Join our exclusive network of trusted suppliers and service providers. We're looking for partners who share our commitment to excellence and innovation.
                      </p>
                    </div>
                    
                    <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                      <div className="flex items-center text-white/90 text-sm sm:text-base">
                        <div className="w-2 h-2 bg-white rounded-full mr-3 sm:mr-4 flex-shrink-0"></div>
                        <span>Global reach with local expertise</span>
                      </div>
                      <div className="flex items-center text-white/90 text-sm sm:text-base">
                        <div className="w-2 h-2 bg-white rounded-full mr-3 sm:mr-4 flex-shrink-0"></div>
                        <span>Competitive compensation terms</span>
                      </div>
                      <div className="flex items-center text-white/90 text-sm sm:text-base">
                        <div className="w-2 h-2 bg-white rounded-full mr-3 sm:mr-4 flex-shrink-0"></div>
                        <span>Long-term partnership opportunities</span>
                      </div>
                    </div>
                    
                    <Button 
                      variant="white"
                      size="lg"
                      onClick={() => setIsRegistrationFormOpen(true)}
                      className="bg-white text-black hover:bg-white/90 transition-all duration-300 hover:scale-105 w-full sm:w-auto"
                    >
                      {t("procurement.register")}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Right Card - Smaller */}
              <div className="lg:col-span-5">
                <div className="h-full bg-white border-2 border-black p-4 sm:p-6 md:p-8 lg:p-10 rounded-2xl sm:rounded-3xl shadow-lg relative overflow-hidden group hover:shadow-2xl transition-all duration-500">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-black/5 rounded-full blur-lg"></div>
                  
                  <div className="relative z-10">
                    <div className="mb-4 sm:mb-6">
                      <div className="w-8 sm:w-12 h-1 bg-black mb-4 sm:mb-6"></div>
                      <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-light mb-3 sm:mb-4 text-black">Current Opportunities</h2>
                      <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">
                        Explore available procurement opportunities across our diverse portfolio of properties and projects.
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 sm:p-6 rounded-xl sm:rounded-2xl mb-4 sm:mb-6">
                      <h4 className="font-semibold text-black mb-2 text-sm sm:text-base">Active Tenders</h4>
                      <div className="text-2xl sm:text-3xl font-light text-black mb-1">12</div>
                      <p className="text-gray-600 text-xs sm:text-sm">Open procurement requests</p>
                    </div>
                    
                    <Button 
                      variant="black"
                      size="lg"
                      className="w-full bg-black text-white hover:bg-gray-900 transition-all duration-300"
                    >
                      View Opportunities
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Process Section - Mobile Optimized */}
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <div className="w-12 sm:w-16 md:w-20 h-1 bg-black mx-auto mb-4 sm:mb-6 md:mb-8"></div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-black mb-3 sm:mb-4 md:mb-6">Procurement Process</h2>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-700 max-w-xl md:max-w-2xl mx-auto px-4">
                A streamlined approach to building lasting partnerships
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12 md:mb-16">
              {[
                {
                  number: "01",
                  title: "Registration",
                  description: "Submit your company details, certifications, and capabilities through our comprehensive registration portal."
                },
                {
                  number: "02", 
                  title: "Evaluation",
                  description: "Our procurement team reviews your qualifications, experience, and alignment with our standards."
                },
                {
                  number: "03",
                  title: "Partnership",
                  description: "Join our approved supplier network and start collaborating on exciting projects across the region."
                }
              ].map((step, index) => (
                <div key={step.number} className="relative group">
                  <div className="bg-white border border-black/20 p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl h-full hover:shadow-xl transition-all duration-500 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                    
                    <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-black/20 mb-3 sm:mb-4">{step.number}</div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-medium text-black mb-2 sm:mb-3 md:mb-4">{step.title}</h3>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{step.description}</p>
                  </div>
                  
                  {index < 2 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-black/30 transform -translate-y-1/2"></div>
                  )}
                </div>
              ))}
            </div>

            {/* Requirements Section - Mobile Optimized */}
            <div className="bg-black text-white p-4 sm:p-6 md:p-8 lg:p-12 rounded-2xl sm:rounded-3xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 sm:w-40 sm:h-40 bg-white/5 rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-16 h-16 sm:w-32 sm:h-32 bg-white/10 rounded-full blur-xl"></div>
              
              <div className="relative z-10 max-w-4xl">
                <div className="w-8 sm:w-12 md:w-16 h-1 bg-white mb-4 sm:mb-6 md:mb-8"></div>
                <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light mb-4 sm:mb-6 md:mb-8">What We Look For</h3>
                
                <div className="grid grid-cols-1 gap-4 sm:gap-6 md:gap-8 md:grid-cols-2">
                  {[
                    "Quality products and services that meet our high standards",
                    "Competitive pricing and exceptional value for money", 
                    "Reliable delivery and consistent service excellence",
                    "Commitment to sustainability and ethical practices",
                    "Professional customer service and dedicated support",
                    "Innovation and adaptability in changing markets"
                  ].map((item, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-white rounded-full mt-1.5 sm:mt-2 mr-3 sm:mr-4 flex-shrink-0"></div>
                      <p className="text-sm sm:text-base text-white/90 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
      
      <SupplierRegistrationForm 
        open={isRegistrationFormOpen}
        onOpenChange={setIsRegistrationFormOpen}
      />
    </div>
  );
};

export default Procurement;